import { useContext, useState } from "react";
import { Modal } from "semantic-ui-react";
import moment from "moment-timezone";
import ModalCenterTemplate from "../../../../../../../App/views/ModalCenterTemplate";
import { PUBLIC_URL } from "../../../../../../../App/configs/Constants";
import ButtonCustom from "../../../../../../../../components/Button";
import { SweetAlertCommon } from "../../../../../../../../components/SweetAlert";
import { SWAL_DELETE_OPTION } from "../../../../../../../../components/SweetAlert/configs/SwalConfigs";
import {
  toastError,
  toastSuccess,
} from "../../../../../../../../components/Toast";
import { LocalizationContext } from "../../../../../../../../locales/Translation";
import {
  cancelExtraServiceService,
  cancelAllExtraServicesForBookingService,
} from "../../../../../../services/DashboardServices";
import {
  ExtraServiceType,
  TotalExtraServicesFeeType,
} from "../../../../../../configs/DeclareTypes";
import { EXTRA_SERVICE_PENDING_STATUS } from "../../../../../../configs/Constants";
import "./style.scss";

const DATE_FORMAT = "MMM DD, YYYY · h:mm A";

type Props = {
  bookingId: number;
  bookingType: string;
  extraServices?: ExtraServiceType[];
  totalExtraServicesFee?: TotalExtraServicesFeeType;
  onCancelled?: () => void;
};

const ExtraServicesModal = ({
  bookingId,
  bookingType,
  extraServices = [],
  totalExtraServicesFee,
  onCancelled,
}: Props) => {
  const { translations } = useContext(LocalizationContext);
  const [open, setOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [cancellingAll, setCancellingAll] = useState(false);

  if (!extraServices.length || !totalExtraServicesFee) {
    return null;
  }

  const paid = Number(totalExtraServicesFee.paid || 0);
  const pending = Number(totalExtraServicesFee.pending || 0);
  const totalFee = paid + pending;
  const currency = extraServices[0]?.currency || "";
  const cancellableServices = extraServices.filter(
    (service) => service.status === EXTRA_SERVICE_PENDING_STATUS
  );
  const isBusy = cancellingId !== null || cancellingAll;

  const _onCancelService = (service: ExtraServiceType) => {
    const options = SWAL_DELETE_OPTION({
      title: `${translations.cancel} "${service.name}"?`,
      text: translations.cancelExtraServiceConfirmText,
      confirmText: translations.delete,
      cancelText: translations.cancel,
    });
    SweetAlertCommon({
      options,
      confirmCallback: async () => {
        setCancellingId(service.id);
        try {
          await cancelExtraServiceService(service.id);
          toastSuccess(translations.extraServiceCancelledSuccess);
          onCancelled?.();
        } catch (e: any) {
          toastError(e?.status?.message);
        } finally {
          setCancellingId(null);
        }
      },
      cancelCallback: () => null,
    });
  };

  const _onCancelAll = () => {
    const options = SWAL_DELETE_OPTION({
      title: translations.cancelAllExtraServicesTitle,
      text: `${translations.cancelAllExtraServicesTextPrefix} ${cancellableServices.length} ${translations.cancelAllExtraServicesTextSuffix}`,
      confirmText: translations.delete,
      cancelText: translations.cancel,
    });
    SweetAlertCommon({
      options,
      confirmCallback: async () => {
        setCancellingAll(true);
        try {
          await cancelAllExtraServicesForBookingService(bookingType, bookingId);
          toastSuccess(translations.allExtraServicesCancelledSuccess);
          onCancelled?.();
        } catch (e: any) {
          toastError(e?.status?.message);
        } finally {
          setCancellingAll(false);
        }
      },
      cancelCallback: () => null,
    });
  };

  return (
    <>
      <div className={"extra-service-fee-trigger"}>
        <div className={"table_title"}>{translations.extraServiceFee}:</div>
        <div className={"extra-service-fee-trigger__amount"}>
          <span>
            {currency} {totalFee.toFixed(2)}
          </span>
          <img
            className={"extra-service-fee-trigger__icon"}
            src={PUBLIC_URL + "/assets/icons/icon-message-blue.svg"}
            onClick={() => setOpen(true)}
          />
        </div>
        {pending > 0 ? (
          <div className={"pending-chip"}>
            {translations.pending} {currency} {pending.toFixed(2)}
          </div>
        ) : null}
      </div>

      <ModalCenterTemplate
        trigger={null}
        open={open}
        onClose={() => setOpen(false)}
        modalClassName={"modal-general extra-services-modal"}
      >
        <Modal.Header>
          <span className={"extra-services-modal__title"}>
            {translations.extraServices}
          </span>
          <ButtonCustom
            onClick={() => setOpen(false)}
            className={"modal-close-cta"}
          >
            <>
              <span>{translations.close}</span>
              <img src={PUBLIC_URL + "/assets/icons/icon-close-grey.svg"} />
            </>
          </ButtonCustom>
        </Modal.Header>
        <Modal.Content>
          <div className={"extra-services-modal__subtitle"}>
            {translations.showing} {extraServices.length}{" "}
            {translations.extraService}
            {extraServices.length > 1 ? "s" : ""}
          </div>

          <div className={"extra-services-modal__list"}>
            {extraServices.map((service) => (
              <div key={service.id} className={"extra-services-modal__item"}>
                <div className={"extra-services-modal__item-row-1"}>
                  <div className={"extra-services-modal__item-title"}>
                    <span className={"extra-services-modal__item-name"}>
                      {service.name}
                    </span>
                    <span
                      className={`extra-services-modal__item-status ${service.status}`}
                    >
                      {service.status}
                    </span>
                  </div>
                  <div className={"extra-services-modal__item-price"}>
                    {service.currency} {service.total_price}
                  </div>
                </div>
                <div className={"extra-services-modal__item-row-2"}>
                  <div className={"extra-services-modal__item-meta"}>
                    <div>
                      {service.quantity} {service.unit_name} ×{" "}
                      {service.currency} {service.unit_price}
                    </div>
                    {service.delivery_time ? (
                      <div>
                        {translations.delivery}{" "}
                        {moment(service.delivery_time).format(DATE_FORMAT)}
                      </div>
                    ) : null}
                  </div>
                  {service.status === EXTRA_SERVICE_PENDING_STATUS ? (
                    <button
                      type={"button"}
                      className={"extra-services-modal__item-cancel-icon"}
                      disabled={isBusy}
                      onClick={() => _onCancelService(service)}
                      title={translations.cancelServiceTooltip}
                    >
                      <i className="uil uil-trash-alt" />
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <div className={"extra-services-modal__actions-row"}>
            {cancellableServices.length > 1 ? (
              <ButtonCustom
                className={"danger-alt"}
                loading={cancellingAll}
                disabled={isBusy}
                onClick={_onCancelAll}
              >
                {translations.cancelAllServicesButton}
              </ButtonCustom>
            ) : null}
            <ButtonCustom className={"primary"} onClick={() => setOpen(false)}>
              {translations.close}
            </ButtonCustom>
          </div>
        </Modal.Actions>
      </ModalCenterTemplate>
    </>
  );
};

export default ExtraServicesModal;
